/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { When } from "react-if";
import { theme, Button, FlexContainer } from "@nordcloud/gnui";
import { BaselineTag, ActionNameDisplay } from "~/components";
import { ValueWrapper } from "~/components/Details/styles";
import { PlanEntity } from "../../../types";
import { PlanActionNameInput } from "./PlanActionNameInput";

type Props = {
  item: PlanEntity;
  isEditing: boolean;
  tag?: "source" | "dependent";
  onEditClick: () => void;
  handlePlanErrors: () => void;
  onEditComplete: () => void;
};

export function EditableActionName({
  item,
  isEditing,
  tag,
  onEditClick,
  handlePlanErrors,
  onEditComplete,
}: Props) {
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const renderEditableName = (name: string) => (
    <span
      css={{
        display: "flex",
        paddingRight: theme.spacing.spacing07,
        position: "relative",
        alignItems: "center",
      }}
      onMouseOver={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
    >
      <ValueWrapper editable={!isHovered} isEmpty={false}>
        {name}
      </ValueWrapper>
      <When condition={isHovered}>
        <Button
          aria-label="edit button"
          icon="edit"
          severity="low"
          size="sm"
          css={{ marginLeft: theme.spacing.spacing02 }}
          onClick={() => {
            onEditClick();
            setIsHovered(false);
          }}
        />
      </When>
    </span>
  );

  return isEditing ? (
    <PlanActionNameInput
      item={item}
      handlePlanErrors={handlePlanErrors}
      onEditComplete={onEditComplete}
    />
  ) : (
    <FlexContainer direction="row" justifyContent="space-between">
      <div>
        <ActionNameDisplay
          actionName={item.action?.name || item.actionName || item.name || ""}
          customName={item.name || ""}
          actionType={item.actionType}
          isActionBatch={!!item.batchActions}
          renderEditableName={renderEditableName}
        />
      </div>
      {tag && <BaselineTag tag={tag} />}
    </FlexContainer>
  );
}
